import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip as RechartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART, TOKENS } from "../../lib/designTokens";
import { fmtDate } from "../../lib/format";
import { classifyChartSeries } from "./progressChartState";
import { ProgressChartContainer } from "./ProgressChartContainer";
import { ProgressChartTooltip } from "./ProgressChartTooltip";
import { ProgressSectionState } from "./ProgressSectionStatus";
import {
  PROGRESS_AXIS_LINE,
  PROGRESS_CHART_GRID_PROPS,
  PROGRESS_CHART_MARGIN,
  PROGRESS_CHART_TICK,
  PROGRESS_HISTORY_Y_AXIS_WIDTH,
  CHART_MIN_HEIGHT,
  buildNumericDomain,
  formatChartDateTick,
  formatTooltipNumber,
  getXAxisInterval,
  lineSeriesDot,
} from "./progressChartUtils";

export const ProgressHistoryChart = ({ title, unit, data, emptyMessage, testId }) => {
  const chartState = classifyChartSeries(data, "value");
  const domain = buildNumericDomain(data, ["value"], { includeZero: true });
  const lineColor = title?.toLowerCase().includes("ftp") || title?.toLowerCase().includes("w/kg")
    ? TOKENS.analytics.amber
    : TOKENS.analytics.blue;

  if (chartState === "empty" || chartState === "all-null") {
    return (
      <div className="app-card-elevated p-4 sm:p-5 min-w-0" data-testid={testId}>
        <div className="card-label">{title}</div>
        <ProgressSectionState
          variant={chartState === "all-null" ? "unavailable" : "empty"}
          description={emptyMessage}
          testId={`${testId}-empty`}
          className="mt-4 border-0 bg-transparent p-0"
        />
      </div>
    );
  }

  return (
    <div className="app-card-elevated p-4 sm:p-5 min-w-0" data-testid={testId}>
      <div className="card-label mb-3">{title}</div>
      <ProgressChartContainer
        title={`${title} chart`}
        ariaLabel={`${title} line chart in ${unit}`}
        testId={`${testId}-plot`}
        chartState={chartState}
        minHeight={CHART_MIN_HEIGHT.history}
        summary={`${title} with ${data.length} entries.`}
      >
        <LineChart data={data} margin={PROGRESS_CHART_MARGIN}>
          <CartesianGrid {...PROGRESS_CHART_GRID_PROPS} />
          <XAxis
            dataKey="date"
            tick={PROGRESS_CHART_TICK}
            tickLine={false}
            axisLine={PROGRESS_AXIS_LINE}
            interval={getXAxisInterval(data.length)}
            minTickGap={10}
            tickFormatter={(value, index) => formatChartDateTick(value, index, data.length)}
          />
          <YAxis
            tick={PROGRESS_CHART_TICK}
            tickLine={false}
            axisLine={PROGRESS_AXIS_LINE}
            width={PROGRESS_HISTORY_Y_AXIS_WIDTH}
            domain={domain}
          />
          <RechartTooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0]?.payload;
              const formatted = formatTooltipNumber(row?.value, { decimals: 2, unit });
              return (
                <ProgressChartTooltip
                  active
                  payload={payload}
                  label={row?.date ? fmtDate(row.date) : null}
                  rows={formatted != null ? [{ label: title, value: formatted }] : []}
                />
              );
            }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={lineColor}
            strokeWidth={2.25}
            dot={lineSeriesDot(data.length, lineColor)}
            activeDot={{ r: 5, fill: lineColor, stroke: CHART.dotStroke, strokeWidth: 1.5 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ProgressChartContainer>
    </div>
  );
};
